import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import YhSelect from "../ui/YhSelect";
import YhInput from "../ui/YhInput";
import YhButton from "../ui/YhButton";
import {
  listOrgLeadSources,
  upsertOrgLeadSource,
} from "../services/organizationService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../helpers/methods";

export type SourceType = "bulk" | "manual";

export function sourceTypeToGroup(type: SourceType): "bulk_upload" | "other" {
  return type === "bulk" ? "bulk_upload" : "other";
}

type Props = {
  organizationId: string;
  sourceType: SourceType;
  subSource: string;
  onSourceTypeChange: (v: SourceType) => void;
  onSubSourceChange: (v: string) => void;
  required?: boolean;
  /** When true, shows helper that value is applied to every imported row */
  applyToAllHint?: boolean;
};

/**
 * Source = Bulk | Manual (maps to lead_sources.sourceGroup).
 * Sub-source = selectable/creatable source_name stored on Lead.source / CP.source.
 * Unknown names are created in the org master list automatically.
 */
const SourceSubSourceFields: React.FC<Props> = ({
  organizationId,
  sourceType,
  subSource,
  onSourceTypeChange,
  onSubSourceChange,
  required = true,
  applyToAllHint = true,
}) => {
  const [sources, setSources] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const ensuredKeysRef = useRef<Set<string>>(new Set());

  const loadSources = useCallback(async () => {
    if (!organizationId) {
      setSources([]);
      return;
    }
    setLoading(true);
    try {
      const res = await listOrgLeadSources(organizationId);
      const list = res?.data?.items || res?.data || res?.items || [];
      setSources(Array.isArray(list) ? list : []);
    } catch (_) {
      setSources([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  const group = sourceTypeToGroup(sourceType);

  const options = useMemo(() => {
    const filtered = (sources || []).filter((s) => {
      if (s.isActive === false || String(s.status) === "0") return false;
      const g = String(s.sourceGroup || "other");
      return g === group;
    });
    const opts = [
      { label: loading ? "Loading…" : "Select sub-source…", value: "" },
      ...filtered.map((s) => ({
        label: s.source_name,
        value: String(s.source_name),
      })),
    ];
    // Keep a selected / pending value visible even before master list refreshes
    if (
      subSource &&
      !opts.some(
        (o) =>
          String(o.value).toLowerCase() === String(subSource).toLowerCase()
      )
    ) {
      opts.push({ label: `${subSource} (new)`, value: subSource });
    }
    return opts;
  }, [sources, group, loading, subSource]);

  // If selected sub-source is not in master yet, create it (don't wipe the value)
  useEffect(() => {
    const name = String(subSource || "").trim();
    if (!organizationId || !name) return;

    const key = `${organizationId}:${group}:${name.toLowerCase()}`;
    if (ensuredKeysRef.current.has(key)) return;

    const exists = (sources || []).some(
      (s) =>
        String(s.source_name || "")
          .trim()
          .toLowerCase() === name.toLowerCase()
    );
    if (exists) {
      ensuredKeysRef.current.add(key);
      return;
    }

    ensuredKeysRef.current.add(key);
    let cancelled = false;
    (async () => {
      try {
        await upsertOrgLeadSource(organizationId, {
          source_name: name,
          sourceGroup: group,
        });
        if (!cancelled) await loadSources();
      } catch (_) {
        ensuredKeysRef.current.delete(key);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [subSource, organizationId, group, sources, loadSources]);

  const createSource = async (rawName: string) => {
    const name = rawName.trim();
    if (!organizationId) {
      openErrorNotification("Select an organization first");
      return;
    }
    if (!name) {
      openErrorNotification("Enter a sub-source name");
      return;
    }
    setCreating(true);
    try {
      await upsertOrgLeadSource(organizationId, {
        source_name: name,
        sourceGroup: group,
      });
      ensuredKeysRef.current.add(
        `${organizationId}:${group}:${name.toLowerCase()}`
      );
      openSuccessNotification(`Sub-source “${name}” saved`);
      setNewName("");
      await loadSources();
      onSubSourceChange(name);
    } catch (e: any) {
      openErrorNotification(e?.message || "Could not create sub-source");
    } finally {
      setCreating(false);
    }
  };

  const handleCreate = async () => {
    await createSource(newName);
  };

  return (
    <div className="space-y-3 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-3">
      <p className="text-sm font-medium text-[#111827]">
        Source &amp; sub-source{required ? " *" : " (optional)"}
      </p>
      {applyToAllHint ? (
        <p className="text-xs text-[#6B7280]">
          Applied to every row in this upload (stored on each lead / channel
          partner). Leave blank to use values from the file when mapped — new
          sources are created automatically.
        </p>
      ) : null}
      <YhSelect
        label={required ? "Source" : "Source (optional)"}
        clearable={!required}
        value={sourceType}
        onChange={(e: any) => {
          const v = String(e?.target?.value || "bulk") as SourceType;
          onSourceTypeChange(v === "manual" ? "manual" : "bulk");
        }}
        options={[
          { label: "Bulk", value: "bulk" },
          { label: "Manual", value: "manual" },
        ]}
      />
      <YhSelect
        label={required ? "Sub-source" : "Sub-source (optional)"}
        clearable
        value={subSource}
        onChange={(e: any) =>
          onSubSourceChange(String(e?.target?.value || ""))
        }
        options={options}
      />
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[180px] flex-1">
          <YhInput
            label="Create new sub-source"
            value={newName}
            onChange={(e: any) => setNewName(e.target?.value ?? e)}
            placeholder="e.g. Facebook Ads, Walk-in"
            onKeyDown={(e: any) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void createSource(newName);
              }
            }}
          />
        </div>
        <YhButton
          variant="secondary"
          size="sm"
          disabled={creating || !organizationId}
          onClick={handleCreate}
        >
          {creating ? "Saving…" : "Add sub-source"}
        </YhButton>
      </div>
      {!organizationId ? (
        <p className="text-xs text-[#B45309]">
          Select an organization to load / create sub-sources.
        </p>
      ) : null}
    </div>
  );
};

export default SourceSubSourceFields;
